import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { AlertTriangle, Plus, UserPlus, Building2, Info } from "lucide-react";

export const ENQUIRY_SOURCES = ["Phone Call", "WhatsApp", "Email", "Walk-in", "Referral", "Website", "Trade Show / Event", "Social Media", "Repeat Client", "Other"];
export const ENQUIRY_STATUS_OPTS = ["Incomplete", "New", "Contacted", "Requirement Understood", "Proposal In Progress", "Proposal Shared", "Follow-up", "Confirmed", "Closed", "Lost"];

const HOTEL_CATS = ["Budget", "3 Star", "4 Star", "5 Star", "Luxury / Boutique", "Mixed"];
const MEALS = ["Room Only", "Breakfast", "Half Board (MAP)", "Full Board (AP)", "As per plan"];

const blank = {
  enquiry_source: "", is_direct: false, partner_id: "", partner_contact_id: "", status: "New", assigned_to: "",
  agency_name: "", agent_name: "", mobile: "", whatsapp: "", email: "",
  destinations: "", travel_dates: "", nights: "", starting_city: "", travel_purpose: "",
  adults: "", children: "", children_ages: "", rooms: "", hotel_category: "", meal_preference: "",
  budget: "", transportation: "", preferred_response: "", needs_design_help: false,
  next_follow_up: "", follow_up_note: "", special_requirements: "",
};

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="font-accent text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>
);

export function ManualEnquiryDialog({ open, onClose, onSaved, assignables = [], editData = null }) {
  const [form, setForm] = useState(blank);
  const [partners, setPartners] = useState([]);
  const [partnerContacts, setPartnerContacts] = useState([]);
  const [dupes, setDupes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPartnerCreate, setShowPartnerCreate] = useState(false);
  const [showContactCreate, setShowContactCreate] = useState(false);
  const isEdit = !!editData;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadPartners = useCallback(() => { api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!open) return;
    loadPartners();
    setDupes([]);
    if (editData) {
      const merged = { ...blank };
      Object.keys(blank).forEach((k) => { if (editData[k] !== undefined && editData[k] !== null) merged[k] = editData[k]; });
      merged.assigned_to = editData.assigned_to || "";
      merged.status = editData.status || "New";
      setForm(merged);
      if (editData.partner_id) loadPartnerContacts(editData.partner_id);
    } else {
      setForm(blank); setPartnerContacts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadPartnerContacts = async (pid) => {
    try { const { data } = await api.get(`/admin/partners/${pid}/contact-persons`); setPartnerContacts(data || []); }
    catch { setPartnerContacts([]); }
  };

  const onSelectPartner = async (pid) => {
    if (pid === "none") { setForm((f) => ({ ...f, partner_id: "", partner_contact_id: "" })); setPartnerContacts([]); return; }
    const p = partners.find((x) => x.id === pid);
    setForm((f) => ({ ...f, partner_id: pid, is_direct: false, partner_contact_id: "", agency_name: p?.agency_name || f.agency_name }));
    await loadPartnerContacts(pid);
  };

  const onSelectContact = (cid) => {
    if (cid === "none") { set("partner_contact_id", ""); return; }
    const c = partnerContacts.find((x) => x.id === cid);
    if (c) setForm((f) => ({ ...f, partner_contact_id: cid, agent_name: c.name || f.agent_name, mobile: c.mobile || f.mobile, whatsapp: c.whatsapp || f.whatsapp, email: c.email || f.email }));
  };

  const checkDupes = async () => {
    if (isEdit) return;
    const email = form.email.trim(), mobile = form.mobile.trim();
    if (!email && !mobile) { setDupes([]); return; }
    try { const { data } = await api.get("/admin/check-duplicate", { params: { type: "enquiry", email, mobile } }); setDupes(data.duplicates || []); }
    catch { setDupes([]); }
  };

  const buildPayload = (incomplete) => {
    const p = { ...form };
    ["adults", "children", "nights", "rooms"].forEach((k) => { p[k] = p[k] === "" ? 0 : Number(p[k]); });
    p.assigned_to = form.assigned_to || null;
    p.partner_id = form.partner_id || null;
    p.next_follow_up = form.next_follow_up || null;
    p.save_incomplete = incomplete;
    if (incomplete) delete p.status;  // let backend set "Incomplete"
    return p;
  };

  const submit = async (incomplete) => {
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/admin/enquiries/${editData.id}`, buildPayload(incomplete));
        toast.success(data.is_complete ? "Enquiry updated" : "Saved (still incomplete)");
      } else {
        const { data } = await api.post("/admin/enquiries", buildPayload(incomplete));
        toast.success(`Enquiry ${data.reference} created`);
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="manual-enquiry-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? `Edit Enquiry ${editData.reference || ""}` : "New Enquiry"}</DialogTitle></DialogHeader>

        {dupes.length > 0 && (
          <div className="rounded-lg border border-saffron/40 bg-saffron/10 p-3" data-testid="duplicate-warning">
            <div className="flex items-center gap-2 font-accent text-xs font-semibold text-saffron"><AlertTriangle className="h-4 w-4" />Possible duplicate enquiry found</div>
            <ul className="mt-1 space-y-0.5 text-xs text-charcoal">
              {dupes.slice(0, 4).map((d) => <li key={d.id}>{d.reference} · {d.agency_name || d.agent_name} · {d.email || d.mobile} · {d.status}</li>)}
            </ul>
          </div>
        )}

        <Accordion type="multiple" defaultValue={["basic", "requirement"]} className="w-full">
          {/* 1. BASIC DETAILS */}
          <AccordionItem value="basic">
            <AccordionTrigger data-testid="section-basic" className="font-accent font-semibold text-charcoal">1. Basic Details</AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Enquiry Source *">
                  <Select value={form.enquiry_source || undefined} onValueChange={(v) => set("enquiry_source", v)}>
                    <SelectTrigger data-testid="enq-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>{ENQUIRY_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Enquiry Type">
                  <Select value={form.is_direct ? "direct" : "agency"} onValueChange={(v) => set("is_direct", v === "direct")}>
                    <SelectTrigger data-testid="enq-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="agency">Travel Agency / Partner</SelectItem><SelectItem value="direct">Direct Enquiry</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Assigned Team Member *">
                  <Select value={form.assigned_to || undefined} onValueChange={(v) => set("assigned_to", v)}>
                    <SelectTrigger data-testid="enq-assigned"><SelectValue placeholder="Assign to" /></SelectTrigger>
                    <SelectContent>{assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Enquiry Status *">
                  <Select value={form.status || undefined} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="enq-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{ENQUIRY_STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 2. TRAVEL PARTNER & CONTACT */}
          <AccordionItem value="partner">
            <AccordionTrigger data-testid="section-partner" className="font-accent font-semibold text-charcoal">2. Travel Partner &amp; Contact</AccordionTrigger>
            <AccordionContent>
              {!form.is_direct && (
                <div className="mb-3 grid gap-3 sm:grid-cols-2">
                  <Field label="Link Existing Partner">
                    <div className="flex gap-1">
                      <Select value={form.partner_id || "none"} onValueChange={onSelectPartner}>
                        <SelectTrigger data-testid="enq-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">— None —</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.agency_name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" size="icon" variant="outline" data-testid="enq-new-partner-btn" onClick={() => setShowPartnerCreate(true)} title="Create partner"><Building2 className="h-4 w-4" /></Button>
                    </div>
                  </Field>
                  <Field label="Contact Person (from partner)">
                    <div className="flex gap-1">
                      <Select value={form.partner_contact_id || "none"} onValueChange={onSelectContact} disabled={!form.partner_id}>
                        <SelectTrigger data-testid="enq-contact-select"><SelectValue placeholder={form.partner_id ? "Select contact" : "Select a partner first"} /></SelectTrigger>
                        <SelectContent><SelectItem value="none">— None —</SelectItem>{partnerContacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.is_primary ? " (primary)" : ""}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" size="icon" variant="outline" data-testid="enq-new-contact-btn" onClick={() => setShowContactCreate(true)} disabled={!form.partner_id} title="Add contact person"><UserPlus className="h-4 w-4" /></Button>
                    </div>
                  </Field>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={form.is_direct ? "Traveller Name *" : "Agency / Company Name *"}><Input data-testid="enq-agency" value={form.agency_name} onChange={(e) => set("agency_name", e.target.value)} /></Field>
                <Field label="Contact Person *"><Input data-testid="enq-agent" value={form.agent_name} onChange={(e) => set("agent_name", e.target.value)} /></Field>
                <Field label="Mobile"><Input data-testid="enq-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} onBlur={checkDupes} /></Field>
                <Field label="WhatsApp"><Input data-testid="enq-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
                <Field label="Email"><Input data-testid="enq-email" value={form.email} onChange={(e) => set("email", e.target.value)} onBlur={checkDupes} /></Field>
              </div>
              <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground"><Info className="h-3 w-3" />Provide at least a mobile number or email address.</p>
            </AccordionContent>
          </AccordionItem>

          {/* 3. TRAVEL REQUIREMENT */}
          <AccordionItem value="requirement">
            <AccordionTrigger data-testid="section-requirement" className="font-accent font-semibold text-charcoal">3. Travel Requirement</AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Destinations Preferred *"><Input data-testid="enq-destinations" value={form.destinations} onChange={(e) => set("destinations", e.target.value)} /></Field>
                <Field label="Travel Dates"><Input data-testid="enq-dates" placeholder="e.g. 12–18 Dec or flexible" value={form.travel_dates} onChange={(e) => set("travel_dates", e.target.value)} /></Field>
                <Field label="Nights"><Input type="number" min="0" data-testid="enq-nights" value={form.nights} onChange={(e) => set("nights", e.target.value)} /></Field>
                <Field label="Starting City"><Input data-testid="enq-startcity" value={form.starting_city} onChange={(e) => set("starting_city", e.target.value)} /></Field>
                <Field label="Travel Purpose"><Input data-testid="enq-purpose" placeholder="Leisure, Honeymoon, MICE…" value={form.travel_purpose} onChange={(e) => set("travel_purpose", e.target.value)} /></Field>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 4. TRAVELLER & ROOM */}
          <AccordionItem value="travellers">
            <AccordionTrigger data-testid="section-travellers" className="font-accent font-semibold text-charcoal">4. Traveller &amp; Room Details</AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Adults"><Input type="number" min="0" data-testid="enq-adults" value={form.adults} onChange={(e) => set("adults", e.target.value)} /></Field>
                <Field label="Children"><Input type="number" min="0" data-testid="enq-children" value={form.children} onChange={(e) => set("children", e.target.value)} /></Field>
                <Field label="Children Ages"><Input data-testid="enq-childages" placeholder="e.g. 4, 8" value={form.children_ages} onChange={(e) => set("children_ages", e.target.value)} /></Field>
                <Field label="Rooms"><Input type="number" min="0" data-testid="enq-rooms" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} /></Field>
                <Field label="Hotel Category">
                  <Select value={form.hotel_category || undefined} onValueChange={(v) => set("hotel_category", v)}>
                    <SelectTrigger data-testid="enq-hotelcat"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{HOTEL_CATS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Meal Preference">
                  <Select value={form.meal_preference || undefined} onValueChange={(v) => set("meal_preference", v)}>
                    <SelectTrigger data-testid="enq-meal"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{MEALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 5. PREFERENCES & BUDGET */}
          <AccordionItem value="prefs">
            <AccordionTrigger data-testid="section-prefs" className="font-accent font-semibold text-charcoal">5. Preferences &amp; Budget</AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Budget"><Input data-testid="enq-budget" placeholder="e.g. ₹25,000 pp" value={form.budget} onChange={(e) => set("budget", e.target.value)} /></Field>
                <Field label="Transportation"><Input data-testid="enq-transport" placeholder="Sedan, SUV, Tempo…" value={form.transportation} onChange={(e) => set("transportation", e.target.value)} /></Field>
                <Field label="Preferred Response"><Input data-testid="enq-response" placeholder="Call / WhatsApp / Email" value={form.preferred_response} onChange={(e) => set("preferred_response", e.target.value)} /></Field>
                <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="enq-designhelp" checked={form.needs_design_help} onChange={(e) => set("needs_design_help", e.target.checked)} /> Needs itinerary design help</label>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 6. FOLLOW-UP & NOTES */}
          <AccordionItem value="followup">
            <AccordionTrigger data-testid="section-followup" className="font-accent font-semibold text-charcoal">6. Follow-up &amp; Internal Notes</AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Next Follow-up Date"><Input type="date" data-testid="enq-followup" value={form.next_follow_up} onChange={(e) => set("next_follow_up", e.target.value)} /></Field>
                <Field label="Follow-up Note"><Input data-testid="enq-followupnote" value={form.follow_up_note} onChange={(e) => set("follow_up_note", e.target.value)} /></Field>
                <div className="sm:col-span-2"><Field label="Special Requirements / Internal Notes"><Textarea rows={2} data-testid="enq-notes" value={form.special_requirements} onChange={(e) => set("special_requirements", e.target.value)} /></Field></div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 7. ATTACHMENTS */}
          <AccordionItem value="attachments">
            <AccordionTrigger data-testid="section-attachments" className="font-accent font-semibold text-charcoal">7. Attachments</AccordionTrigger>
            <AccordionContent>
              <p className="flex items-center gap-1 text-xs text-muted-foreground"><Info className="h-3 w-3" />Save the enquiry first, then open it to upload private attachments.</p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" data-testid="save-incomplete-btn" disabled={saving} onClick={() => submit(true)} className="rounded-full">
            {isEdit ? "Save (keep incomplete)" : "Save as Incomplete"}
          </Button>
          <Button type="button" data-testid="save-enquiry-btn" disabled={saving} onClick={() => submit(false)} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">
            {isEdit ? "Save Changes" : "Save Enquiry"}
          </Button>
        </DialogFooter>

        <PartnerCreateDialog open={showPartnerCreate} onClose={() => setShowPartnerCreate(false)} onCreated={async (p) => {
          setPartners((prev) => [{ id: p.id, agency_name: p.agency_name }, ...prev.filter((x) => x.id !== p.id)]);
          setForm((f) => ({ ...f, partner_id: p.id, is_direct: false, partner_contact_id: "", agency_name: p.agency_name }));
          await loadPartnerContacts(p.id);
          setShowPartnerCreate(false);
        }} />
        <ContactCreateDialog open={showContactCreate} partnerId={form.partner_id} onClose={() => setShowContactCreate(false)} onCreated={async () => { await loadPartnerContacts(form.partner_id); setShowContactCreate(false); }} />
      </DialogContent>
    </Dialog>
  );
}

const PARTNER_BIZ = ["Travel Agency", "Tour Operator", "Independent Consultant", "Corporate / MICE", "Educational Institution", "Other"];

function PartnerCreateDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ agency_name: "", contact_person: "", mobile: "", email: "", city: "", business_type: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ agency_name: "", contact_person: "", mobile: "", email: "", city: "", business_type: "" }); }, [open]);
  const save = async () => {
    if (!f.agency_name.trim() || !f.contact_person.trim()) { toast.error("Agency name and contact person are required"); return; }
    setSaving(true);
    try { const { data } = await api.post("/admin/partners", f); toast.success("Partner created"); onCreated({ id: data.id, agency_name: data.agency_name }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inline-partner-dialog"><DialogHeader><DialogTitle className="font-display text-xl">Create Travel Partner</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Agency Name *"><Input data-testid="ip-agency" value={f.agency_name} onChange={(e) => setF({ ...f, agency_name: e.target.value })} /></Field>
          <Field label="Contact Person *"><Input data-testid="ip-contact" value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} /></Field>
          <Field label="Mobile"><Input data-testid="ip-mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
          <Field label="Email"><Input data-testid="ip-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="City"><Input data-testid="ip-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
          <Field label="Business Type">
            <Select value={f.business_type || undefined} onValueChange={(v) => setF({ ...f, business_type: v })}>
              <SelectTrigger data-testid="ip-biztype"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{PARTNER_BIZ.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter><Button data-testid="ip-save" disabled={saving} onClick={save} className="rounded-full bg-saffron text-white hover:bg-saffron-dark"><Plus className="mr-1 h-4 w-4" />Create Partner</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactCreateDialog({ open, partnerId, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", designation: "", email: "", mobile: "", whatsapp: "", is_primary: false });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: "", designation: "", email: "", mobile: "", whatsapp: "", is_primary: false }); }, [open]);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Contact name is required"); return; }
    setSaving(true);
    try { await api.post(`/admin/partners/${partnerId}/contact-persons`, f); toast.success("Contact person added"); onCreated(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inline-contact-dialog"><DialogHeader><DialogTitle className="font-display text-xl">Add Contact Person</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name *"><Input data-testid="ic-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Designation"><Input data-testid="ic-designation" value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} /></Field>
          <Field label="Email"><Input data-testid="ic-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Mobile"><Input data-testid="ic-mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
          <Field label="WhatsApp"><Input data-testid="ic-whatsapp" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} /></Field>
          <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="ic-primary" checked={f.is_primary} onChange={(e) => setF({ ...f, is_primary: e.target.checked })} /> Primary contact</label>
        </div>
        <DialogFooter><Button data-testid="ic-save" disabled={saving} onClick={save} className="rounded-full bg-saffron text-white hover:bg-saffron-dark"><Plus className="mr-1 h-4 w-4" />Add Contact</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
